import type { ArchiveResult } from './trash'
import { api, type Page } from './client'

export interface Campaign {
  id: number
  name: string
  status: string
  campaignType?: string
  startDate?: string
  endDate?: string
  isActive: boolean
  createdAt: string
  // TASK-0155 (REQ-104-001): manual-entry cost fields — the API returns a JSON number for a .NET
  // `decimal?`, same pattern already used elsewhere in this frontend (e.g. Lead.estimatedValue).
  totalCost?: number
  perLeadCost?: number
  fixedCost?: number
  variableCost?: number
  // REQ-116-001: the LeadProvider that supplied this campaign — first hop of the
  // Provider -> Campaign -> Lead attribution chain, no longer an orphan master.
  leadProviderId?: number
}

/**
 * Full shape returned by `GET /api/campaigns/{id}` (`CampaignsController.Get`),
 * which serializes the `Campaign` entity directly — no restricted projection,
 * unlike `list()` above. Includes `description`/`source`, which the list
 * projection omits (see TASK-0055: `openEdit` must load this to avoid
 * silently wiping those two fields on save).
 */
export interface CampaignDetail {
  id: number
  legacyId?: number | null
  name: string
  description?: string | null
  status: string
  campaignType?: string | null
  startDate?: string | null
  endDate?: string | null
  source?: string | null
  isActive: boolean
  createdAt: string
  updatedAt?: string | null
  createdBy?: number | null
  // TASK-0155 (REQ-104-001)
  totalCost?: number | null
  perLeadCost?: number | null
  fixedCost?: number | null
  variableCost?: number | null
  // TASK-0197 (REQ-091-001): call-center calling script, free text.
  script?: string | null
  // TASK-0206 (REQ-091-001): maximum number of contact attempts allowed per lead in this campaign.
  maxAttempts?: number | null
  // TASK-0207 (REQ-091-001): the campaign's stated objective, free text.
  objective?: string | null
  // TASK-0217 (REQ-091-001): the campaign's merceological sector, free text (e.g. "Energia",
  // "Telefonia/Fibra") — no vocabulary, same treatment as script/objective above.
  sector?: string | null
  // TASK-0218 (REQ-091-001): the campaign's product/package name, free text — no vocabulary, same
  // treatment as script/objective/sector above.
  product?: string | null
  // TASK-0224 (REQ-091-001): the daily calling window this campaign's operators are allowed to
  // work in — a .NET `TimeOnly?` serializes as a bare "HH:mm:ss" string, no date/timezone
  // component, same reasoning as startDate/endDate's "YYYY-MM-DD" above for their own DateOnly?.
  activityHoursStart?: string | null
  activityHoursEnd?: string | null
  // TASK-0228 (REQ-062-002): Ad Set/Creative level sub-campaign labels, free text — no vocabulary,
  // same treatment as sector/product above. The "configurable Lead Source master table" half of
  // this requirement's residual is out of scope for this task.
  adSet?: string | null
  creative?: string | null
  // REQ-116-001: the LeadProvider that supplied this campaign — plain id, no FK (see
  // Campaign.LeadProviderId's own remarks, backend, for why).
  leadProviderId?: number | null
  // F-143 (REQ-092-001): prodotto del catalogo (id senza FK), accanto al testo libero `product`.
  catalogProductId?: number | null
  // C-05: email automatiche al cliente (spente di default).
  clientReminderEnabled?: boolean
  noShowEmailEnabled?: boolean
}

export interface CampaignForm {
  name: string
  description: string
  campaignType: string
  startDate: string
  endDate: string
  source: string
  // TASK-0197 (REQ-091-001): call-center calling script, free text — same non-optional local-state
  // treatment as description/source above.
  script: string
  // TASK-0155 (REQ-104-001): manual-entry cost fields.
  totalCost?: number
  perLeadCost?: number
  fixedCost?: number
  variableCost?: number
  // TASK-0206 (REQ-091-001): maximum number of contact attempts allowed per lead — same
  // number-in-wire-payload/string-in-local-state pattern as the cost fields above.
  maxAttempts?: number
  // TASK-0207 (REQ-091-001): the campaign's stated objective, free text — same non-optional
  // local-state treatment as description/source/script above.
  objective: string
  // TASK-0217 (REQ-091-001): the campaign's merceological sector, free text — same non-optional
  // local-state treatment as description/source/script/objective above.
  sector: string
  // TASK-0218 (REQ-091-001): the campaign's product/package name, free text — same non-optional
  // local-state treatment as description/source/script/objective/sector above.
  product: string
  // TASK-0224 (REQ-091-001): the daily calling window — kept as "HH:mm" strings in local state,
  // the native value of an `<input type="time">`, sent as-is (System.Text.Json's TimeOnly reader
  // accepts the shorter "HH:mm" form, same as DateOnly? already accepts bare "YYYY-MM-DD").
  activityHoursStart: string
  activityHoursEnd: string
  // TASK-0228 (REQ-062-002): Ad Set/Creative level sub-campaign labels, free text — same
  // non-optional local-state treatment as description/source/script/objective/sector/product above.
  adSet: string
  creative: string
  // REQ-116-001: the LeadProvider that supplied this campaign — sent as a plain number, same
  // wire shape as every other optional id-typed field on this form.
  leadProviderId?: number
  // F-143 (REQ-092-001): prodotto del catalogo; omesso = nessuno.
  catalogProductId?: number
  // C-05
  clientReminderEnabled?: boolean
  noShowEmailEnabled?: boolean
}

/** F-118 (REQ-104-001): una riga dell'anteprima di `POST /api/campaigns/costs/import`. Le chiavi di costo sono i valori caricati (null = invariato). */
export interface CampaignCostImportRow {
  row: number
  campaign: string
  campaignId: number | null
  campaignName: string | null
  status: 'ok' | 'error'
  error: string | null
  totalCost: number | null
  perLeadCost: number | null
  fixedCost: number | null
  variableCost: number | null
}

export interface CampaignCostImportResult {
  dryRun: boolean
  totalRows: number
  okRows: number
  errorRows: number
  applied: boolean
  rows: CampaignCostImportRow[]
}

export const campaignsApi = {
  list: (p?: { search?: string; status?: string; page?: number; pageSize?: number }) => {
    const q = new URLSearchParams()
    if (p?.search) q.set('search', p.search)
    if (p?.status) q.set('status', p.status)
    q.set('page', String(p?.page ?? 1))
    q.set('pageSize', String(p?.pageSize ?? 25))
    return api<Page<Campaign>>(`/api/campaigns?${q}`)
  },
  get: (id: number) => api<CampaignDetail>(`/api/campaigns/${id}`),
  create: (data: Partial<CampaignForm>) =>
    api<{ id: number }>('/api/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: Partial<CampaignForm>) =>
    api<void>(`/api/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  // TASK-0068: mirrors contestsApi.updateStatus (api/performance.ts) — same PATCH .../status shape.
  updateStatus: (id: number, status: string) =>
    api<void>(`/api/campaigns/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  /** F-118: anteprima (`dryRun=true`) o applicazione (`dryRun=false`, tutto o niente) dei costi da un file CSV. */
  importCosts: (csv: string, dryRun: boolean) =>
    api<CampaignCostImportResult>(`/api/campaigns/costs/import?dryRun=${dryRun}`, {
      method: 'POST',
      body: JSON.stringify({ csv }),
    }),
  /** F-104: `200 { archived: true }` se la campagna ha lead (archiviata, lead intatti), `204` se era vuota (rimossa). */
  delete: (id: number) =>
    api<ArchiveResult | undefined>(`/api/campaigns/${id}`, { method: 'DELETE' }),
}
